import { FC, useContext, useState, useEffect, useMemo } from 'react';
import {
  ShoppingCart, RefreshCw, Search, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Package,
  Clock, AlertCircle, CheckCircle2, Eye,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from 'recharts';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';

// ─── Types ────────────────────────────────────────────────────────────────────
interface DailyOrder { date: string; label: string; orders: number; }

interface BuyingItem {
  _id: string;
  name: string;
  imgUri: string | null;
  category: string | null;
  supplier: string | null;
  totalSkus: number;
  warehouseStock: number;
  damagedStock: number;
  daysLeft: number | null;
  minStockAlert: number | null;
  vel7d: number;
  vel14d: number;
  vel30d: number;
  velocity: number;
  orders7d: number;
  orders14d: number;
  orders30d: number;
  dailyOrders: DailyOrder[];
  urgency: 'out-of-stock' | 'critical' | 'high' | 'medium' | 'no-sales' | 'ok';
  action: 'RESTOCK_NOW' | 'RESTOCK_SOON' | 'PLAN_ORDER' | 'REVIEW' | 'MONITOR';
  recommendedQty: number;
  targetDays: number;
  buffer: number;
  oldBuyingRate: number;
  newMarketPrice: number | null;
  priceToBuy: number;
  priceChange: number;
  priceChangePct: number;
  priceDirection: 'up' | 'down' | 'stable';
  costDelta: number;
  transportFees: number;
  storageFees: number;
  packageFees: number;
  jumiaCommission: number;
  fraisTraitement: number;
  marketNotes: string | null;
}

interface Summary {
  totalGroups: number;
  outOfStock: number;
  critical: number;
  high: number;
  medium: number;
  ok: number;
  noSales: number;
  restockNow: number;
  priceAlerts: number;
}

type FilterKey = 'all' | 'urgent' | 'out-of-stock' | 'critical' | 'high' | 'medium' | 'ok' | 'no-sales';

// ─── Config ───────────────────────────────────────────────────────────────────
const ACTION_CFG: Record<string, { label: string; bg: string; text: string }> = {
  RESTOCK_NOW:  { label: 'Restock Now',  bg: '#dc2626', text: '#fff' },
  RESTOCK_SOON: { label: 'Restock Soon', bg: '#ea580c', text: '#fff' },
  PLAN_ORDER:   { label: 'Plan Order',   bg: '#d97706', text: '#fff' },
  REVIEW:       { label: 'Review',       bg: '#6b7280', text: '#fff' },
  MONITOR:      { label: 'Monitor',      bg: '#16a34a', text: '#fff' },
};

const URGENCY_BORDER: Record<string, string> = {
  'out-of-stock': '#dc2626',
  critical:       '#ef4444',
  high:           '#f97316',
  medium:         '#eab308',
  'no-sales':     '#9ca3af',
  ok:             '#22c55e',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD  = (n: number) => `${Math.round(n).toLocaleString('en-US')} MAD`;
const fmtQty  = (n: number) => n.toLocaleString('en-US');
const fmtVel  = (n: number) => n === 0 ? '0' : n < 1 ? n.toFixed(2) : n.toFixed(1);
const fmtDays = (n: number | null) => {
  if (n === null) return '—';
  if (n >= 999)   return '∞';
  return `${Math.round(n)}d`;
};

const daysColor = (days: number | null, urgency: string) => {
  if (urgency === 'out-of-stock') return '#dc2626';
  if (days === null)  return '#6b7280';
  if (days <= 3)      return '#dc2626';
  if (days <= 7)      return '#f97316';
  if (days <= 14)     return '#eab308';
  return '#16a34a';
};

const chartBarColor = (urgency: string) => {
  if (urgency === 'out-of-stock' || urgency === 'critical') return '#ef4444';
  if (urgency === 'high')   return '#f97316';
  if (urgency === 'medium') return '#eab308';
  return '#6366f1';
};

// ─── Velocity chart tooltip ───────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1f2937', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{label}</div>
      <div>{payload[0].value} orders</div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const KpiCard: FC<{
  label: string; value: string | number; sub?: string;
  accentColor?: string; icon: React.ReactNode;
}> = ({ label, value, sub, accentColor = '#6366f1', icon }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ color: accentColor }}>{icon}</span>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: accentColor, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 5 }}>{sub}</div>}
  </div>
);

const ActionBadge: FC<{ action: string }> = ({ action }) => {
  const cfg = ACTION_CFG[action] || ACTION_CFG.MONITOR;
  return (
    <span style={{
      display: 'inline-block', padding: '5px 12px', borderRadius: 7,
      fontSize: 12, fontWeight: 800, letterSpacing: '0.03em',
      background: cfg.bg, color: cfg.text,
      boxShadow: `0 2px 8px ${cfg.bg}55`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

// ─── Expanded detail panel ────────────────────────────────────────────────────
const PERIODS = [
  { days: 7,  label: '7 Days',  vel: 'vel7d'  as const, orders: 'orders7d'  as const },
  { days: 14, label: '14 Days', vel: 'vel14d' as const, orders: 'orders14d' as const },
  { days: 30, label: '30 Days', vel: 'vel30d' as const, orders: 'orders30d' as const },
];

const DetailPanel: FC<{ item: BuyingItem }> = ({ item }) => {
  const [period, setPeriod] = useState(30);
  const barColor = chartBarColor(item.urgency);

  const chartData = item.dailyOrders.slice(30 - period);
  const maxOrders = Math.max(...chartData.map(d => d.orders), 1);
  const totalInPeriod = chartData.reduce((s, d) => s + d.orders, 0);
  const velInPeriod   = parseFloat((totalInPeriod / period).toFixed(2));

  const chartTopMargin = maxOrders > 0 ? 22 : 8;
  const bottomMargin = period <= 7 ? 10 : period <= 14 ? 28 : 38;

  return (
    <div style={{ padding: '0 12px 16px 12px', borderTop: '1px solid #e5e7eb' }}>

      {/* ── Velocity chart ── */}
      <div style={{ marginTop: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Daily Order Flow</span>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 10 }}>
              {totalInPeriod} orders · {fmtVel(velInPeriod)}/day
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {PERIODS.map(p => (
              <button
                key={p.days}
                onClick={e => { e.stopPropagation(); setPeriod(p.days); }}
                style={{
                  padding: '3px 10px', borderRadius: 6, border: '1px solid',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  borderColor: period === p.days ? barColor : '#e5e7eb',
                  background:  period === p.days ? barColor : '#fff',
                  color:       period === p.days ? '#fff'   : '#6b7280',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: chartTopMargin, right: 8, left: -28, bottom: bottomMargin }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: period <= 7 ? 11 : 9, fill: '#6b7280', fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                interval={0}
                angle={period <= 7 ? 0 : -45}
                textAnchor={period <= 7 ? 'middle' : 'end'}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                domain={[0, maxOrders + 2]}
                hide
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f0f0f0' }} />
              <Bar dataKey="orders" fill={barColor} radius={[4, 4, 0, 0]} maxBarSize={period <= 7 ? 40 : period <= 14 ? 26 : 18}>
                <LabelList
                  dataKey="orders"
                  position="top"
                  formatter={(v: number) => v > 0 ? v : ''}
                  style={{ fontSize: period <= 14 ? 11 : 10, fontWeight: 800, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Detail grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>

        {/* Sales Velocity */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>Sales Velocity</div>
          {([
            { label: 'Last 7 Days',  vel: item.vel7d,  cnt: item.orders7d  },
            { label: 'Last 14 Days', vel: item.vel14d, cnt: item.orders14d },
            { label: 'Last 30 Days', vel: item.vel30d, cnt: item.orders30d },
          ] as const).map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
              <span style={{ color: '#6b7280' }}>{row.label}</span>
              <span style={{ fontWeight: 700, color: '#1f2937' }}>
                {fmtVel(row.vel)}/day ({row.cnt} orders)
              </span>
            </div>
          ))}
        </div>

        {/* Stock Status */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>Stock Status</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
            <span style={{ color: '#6b7280' }}>Warehouse Stock</span>
            <span style={{ fontWeight: 700, color: '#1f2937' }}>{item.warehouseStock} units</span>
          </div>
          {item.damagedStock > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
              <span style={{ color: '#6b7280' }}>Damaged</span>
              <span style={{ fontWeight: 700, color: '#dc2626' }}>{item.damagedStock} units</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 4 }}>
            <span style={{ color: '#6b7280' }}>Days Left</span>
            <span style={{ fontWeight: 800, color: daysColor(item.daysLeft, item.urgency) }}>
              {item.urgency === 'out-of-stock' ? 'Out of Stock' : fmtDays(item.daysLeft)}
            </span>
          </div>
        </div>

        {/* Cost Details */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>Cost Details</div>
          {([
            { label: 'Purchase Price', val: item.priceToBuy },
            { label: 'Shipping',       val: item.transportFees },
            { label: 'Storage',        val: item.storageFees },
            { label: 'Packaging',      val: item.packageFees },
          ] as const).map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
              <span style={{ color: '#6b7280' }}>{row.label}</span>
              <span style={{ color: '#1f2937' }}>{fmtMAD(row.val)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 800, borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 4 }}>
            <span style={{ color: '#374151' }}>Total Cost / Unit</span>
            <span style={{ color: '#1f2937' }}>
              {fmtMAD(item.priceToBuy + item.transportFees + item.storageFees + item.packageFees)}
            </span>
          </div>
          {item.costDelta !== 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
              <span style={{ color: '#9ca3af' }}>vs. old cost</span>
              <span style={{ fontWeight: 700, color: item.costDelta > 0 ? '#dc2626' : '#16a34a' }}>
                {item.costDelta > 0 ? '+' : ''}{fmtMAD(item.costDelta)} / unit
              </span>
            </div>
          )}
        </div>

        {/* Buying Recommendation */}
        <div style={{
          border: `2px solid ${ACTION_CFG[item.action]?.bg || '#6b7280'}`,
          borderRadius: 10, overflow: 'hidden',
        }}>
          <div style={{ background: ACTION_CFG[item.action]?.bg || '#6b7280', padding: '8px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>Buying Recommendation</div>
          </div>
          <div style={{ padding: '10px 14px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
              <span style={{ color: '#6b7280' }}>Units Needed</span>
              <span style={{ fontWeight: 800, fontSize: 20, color: '#1f2937' }}>{fmtQty(item.recommendedQty)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
              <span style={{ color: '#6b7280' }}>Purchase Price / Unit</span>
              <span style={{ fontWeight: 700, color: '#1f2937' }}>{fmtMAD(item.priceToBuy)}</span>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
              Stock after order: {fmtQty(item.warehouseStock + item.recommendedQty)} units ({item.targetDays}d)
            </div>
          </div>
        </div>

        {/* Notes (manual) */}
        {item.marketNotes && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>Notes</div>
            <div style={{ fontSize: 12, color: '#78350f', fontStyle: 'italic' }}>
              {item.marketNotes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Table row ────────────────────────────────────────────────────────────────
const ItemRow: FC<{ item: BuyingItem; expanded: boolean; onToggle: () => void }> = ({ item, expanded, onToggle }) => {
  const borderColor = URGENCY_BORDER[item.urgency] || '#e5e7eb';
  const dc = daysColor(item.daysLeft, item.urgency);

  return (
    <>
      <tr
        style={{ borderLeft: `4px solid ${borderColor}`, cursor: 'pointer', background: expanded ? '#f9fafb' : '#fff' }}
        onClick={onToggle}
      >
        {/* Product */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {item.imgUri ? (
              <img src={item.imgUri} alt="" style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb' }} />
            ) : (
              <div style={{ width: 38, height: 38, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Package size={16} color="#9ca3af" />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190 }}>{item.name}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                {[item.category, item.supplier].filter(Boolean).join(' · ') || `${item.totalSkus} SKU`}
              </div>
            </div>
          </div>
        </td>

        {/* Days Left */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: dc, lineHeight: 1 }}>
            {item.urgency === 'out-of-stock' ? 'Out' : fmtDays(item.daysLeft)}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{item.warehouseStock} units</div>
        </td>

        {/* Velocity */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1f2937' }}>{fmtVel(item.velocity)}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>orders/day</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>{item.orders7d}×7d · {item.orders30d}×30d</div>
        </td>

        {/* Decision */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
          <ActionBadge action={item.action} />
        </td>

        {/* Units to Buy */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
          {item.recommendedQty > 0 ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937' }}>{fmtQty(item.recommendedQty)}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>units</div>
              <div style={{ fontSize: 10, color: '#9ca3af' }}>{item.targetDays}d{item.buffer > 1 ? ` ×${item.buffer}` : ''}</div>
            </>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>
          )}
        </td>

        {/* Buy Price */}
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1f2937' }}>{fmtMAD(item.priceToBuy)}</div>
          {item.priceDirection !== 'stable' && item.newMarketPrice && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, marginTop: 3 }}>
              <span style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through' }}>{fmtMAD(item.oldBuyingRate)}</span>
              {item.priceDirection === 'up'
                ? <ArrowUpRight size={12} color="#dc2626" />
                : <ArrowDownRight size={12} color="#16a34a" />}
              <span style={{ fontSize: 11, fontWeight: 700, color: item.priceDirection === 'up' ? '#dc2626' : '#16a34a' }}>
                {item.priceChangePct > 0 ? '+' : ''}{item.priceChangePct}%
              </span>
            </div>
          )}
        </td>
      </tr>

      {expanded && (
        <tr style={{ background: '#f9fafb', borderLeft: `4px solid ${borderColor}` }}>
          <td colSpan={6}>
            <DetailPanel item={item} />
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const BuyingDecisionsPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore?.token;

  const [summary, setSummary]         = useState<Summary | null>(null);
  const [items, setItems]             = useState<BuyingItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [filter, setFilter]           = useState<FilterKey>('all');
  const [search, setSearch]           = useState('');
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dispatch(actions.getBuyingDecisions, {}, token);
      if (res?.error) {
        setError(res.error.message || 'Error loading data');
      } else if (res?.items) {
        setSummary(res.summary);
        setItems(res.items);
        setLastFetched(new Date());
      } else {
        setError('No data received from server');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const urgentCount = useMemo(() =>
    items.filter(i => i.urgency === 'out-of-stock' || i.urgency === 'critical' || i.urgency === 'high').length,
  [items]);

  const filtered = useMemo(() => {
    return items
      .filter(item => {
        if (filter === 'urgent') return item.urgency === 'out-of-stock' || item.urgency === 'critical' || item.urgency === 'high';
        if (filter === 'all')    return true;
        return item.urgency === filter;
      })
      .filter(item =>
        !search ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (item.category && item.category.toLowerCase().includes(search.toLowerCase())) ||
        (item.supplier  && item.supplier.toLowerCase().includes(search.toLowerCase()))
      );
  }, [items, filter, search]);

  const TABS: { key: FilterKey; label: string; count: number; color?: string }[] = [
    { key: 'all',          label: 'All',           count: items.length },
    { key: 'urgent',       label: 'Urgent',         count: urgentCount,              color: '#dc2626' },
    { key: 'out-of-stock', label: 'Out of Stock',   count: summary?.outOfStock ?? 0, color: '#dc2626' },
    { key: 'critical',     label: 'Critical ≤3d',   count: summary?.critical   ?? 0, color: '#ef4444' },
    { key: 'high',         label: 'This Week ≤7d',  count: summary?.high       ?? 0, color: '#f97316' },
    { key: 'medium',       label: 'Plan ≤14d',      count: summary?.medium     ?? 0, color: '#eab308' },
    { key: 'ok',           label: 'Stable',         count: summary?.ok         ?? 0, color: '#16a34a' },
    { key: 'no-sales',     label: 'Dead Stock',     count: summary?.noSales    ?? 0, color: '#9ca3af' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '24px 20px', fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, background: '#6366f1', color: '#fff', flexShrink: 0 }}>
            <ShoppingCart size={22} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Buying Decisions</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
              Immediate restocking guidance based on sales velocity and stock levels
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastFetched && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              Last updated: {lastFetched.toLocaleTimeString('en-US')}
            </span>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb',
              background: '#fff', fontSize: 13, fontWeight: 700, color: '#374151',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} style={{ animation: loading ? 'bd-spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#991b1b' }}>
          <AlertCircle size={16} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{error}</span>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && !items.length && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: '#9ca3af', flexDirection: 'column', gap: 12 }}>
          <RefreshCw size={28} style={{ animation: 'bd-spin 1s linear infinite' }} />
          <span style={{ fontSize: 14 }}>Loading buying decisions…</span>
        </div>
      )}

      {/* ── KPI Cards ── */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 14, marginBottom: 24 }}>
          <KpiCard label="Restock Now"  value={summary.outOfStock + summary.critical} sub={`${summary.outOfStock} out of stock`} accentColor="#dc2626" icon={<AlertTriangle size={18} />} />
          <KpiCard label="This Week"    value={summary.high}        sub="≤ 7 days of stock"   accentColor="#f97316" icon={<Clock size={18} />} />
          <KpiCard label="Price Alerts" value={summary.priceAlerts} sub="market price changed" accentColor="#d97706" icon={<AlertCircle size={18} />} />
          <KpiCard label="Dead Stock"   value={summary.noSales}     sub="no sales in 30 days"  accentColor="#9ca3af" icon={<Eye size={18} />} />
          <KpiCard label="Stable"       value={summary.ok}          sub="sufficient stock"      accentColor="#16a34a" icon={<CheckCircle2 size={18} />} />
        </div>
      )}

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { color: '#dc2626', label: 'Out / Critical ≤3 days' },
          { color: '#f97316', label: 'High ≤7 days' },
          { color: '#eab308', label: 'Medium ≤14 days' },
          { color: '#22c55e', label: 'Stable' },
          { color: '#9ca3af', label: 'No Sales' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>

      {/* ── Filter tabs + Search ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 8, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>

          {TABS.map(tab => {
            const active = filter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                style={{
                  padding: '5px 12px', borderRadius: 6, border: '1px solid',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.1s',
                  borderColor: active ? (tab.color || '#6366f1') : '#e5e7eb',
                  background:  active ? (tab.color || '#6366f1') : '#fff',
                  color: active ? '#fff' : '#6b7280',
                }}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span style={{
                    marginLeft: 5, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 800,
                    background: active ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                    color: active ? '#fff' : '#6b7280',
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}

          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', minWidth: 190, marginLeft: 'auto' }}>
            <Search size={14} color="#9ca3af" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products…"
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#374151', width: '100%' }}
            />
          </div>
        </div>

        {/* ── Table ── */}
        {filtered.length === 0 && !loading ? (
          <div style={{ padding: '50px 20px', textAlign: 'center', color: '#9ca3af' }}>
            <Package size={36} style={{ margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 14, fontWeight: 600 }}>No products match this filter</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {[
                    { label: 'Product',      align: 'left'   as const },
                    { label: 'Days Left',    align: 'center' as const },
                    { label: 'Velocity',     align: 'center' as const },
                    { label: 'Decision',     align: 'center' as const },
                    { label: 'Units to Buy', align: 'center' as const },
                    { label: 'Buy Price',    align: 'center' as const },
                  ].map(col => (
                    <th key={col.label} style={{ padding: '9px 12px', fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', textAlign: col.align, whiteSpace: 'nowrap' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <ItemRow
                    key={item._id}
                    item={item}
                    expanded={expandedId === item._id}
                    onToggle={() => setExpandedId(prev => prev === item._id ? null : item._id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {filtered.length} products — click any row for details and chart
            </span>
            {filter !== 'all' && (
              <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 700, cursor: 'pointer' }} onClick={() => setFilter('all')}>
                Show All →
              </span>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes bd-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        tbody tr:hover td { background: #f3f4f6 !important; }
      `}</style>
    </div>
  );
};

export default BuyingDecisionsPage;
